import { useCallback, useState } from 'react'
import { Button, Input, TextArea } from '@heroui/react'
import { api } from '../lib/api'
import type { NotificationsView, SMSGateway } from '../lib/types'
import { Icon } from '../ui/icons'
import { Confirm, Toggle, useSave } from './sectionKit'
import { ChoiceSelect, InnerPage, Notice, Seg, SettingRow } from './settingsKit'
import '../styles/org.css'
import '../styles/orgset.css'

/**
 * The SMS gateway: where an organisation's texts go, and one test text through it.
 * The installation owner's alone, because whoever sets the address receives every
 * customer's number. English, as the owner's other screens are (Ruling 8 of batch
 * 3 part A, admin/DirectoryOwner.tsx).
 *
 * Drawn as the Gateway row of Text messages ("Sent through <host>"), whose Edit opens
 * the gateway's own page (#notifications/gateway); an organisation's administrator
 * sees the same row as a plain fact (Notifications.tsx).
 *
 * The secret is never shown. Leaving it empty keeps the stored one; "Clear secret"
 * removes it on Save. It is asked for again when the address moves to another host.
 *
 * "Remove gateway" forgets the address and the secret and gives up every text still
 * waiting. Asked twice, as cancelling a booking is (pages/Appointments.tsx), because
 * texts stop at once. It works under a lapsed licence: stopping where customers'
 * numbers go is never something a renewal should stand in front of.
 */
export function NotificationsOwner({ view, onEdit }: { view: NotificationsView; onEdit: () => void }) {
  return (
    <>
      <SettingRow
        control
        label={view.gatewayConfigured ? `Sent through ${view.gatewayHost}` : 'No gateway set'}
        status={view.secretSet ? 'Secret stored' : 'No secret stored'}
      >
        <Button variant={view.gatewayConfigured ? 'secondary' : 'primary'} size="sm" onPress={onEdit}>
          {view.gatewayConfigured ? (
            <>
              <Icon name="edit" size={14} /> Edit
            </>
          ) : (
            'Set up'
          )}
        </Button>
      </SettingRow>
    </>
  )
}

export function GatewayDrawer({
  back,
  organisation,
  view,
  onSaved,
  onClose,
  flash,
}: {
  /** The section's name, the page's way back. */
  back: string
  organisation?: string
  view: NotificationsView
  onSaved: () => Promise<void>
  onClose: () => void
  flash: (text: string) => void
}) {
  const [gw, setGw] = useState<SMSGateway>(view.gateway?.method ? view.gateway : { method: 'POST', url: '', contentType: 'application/json' })
  const [secret, setSecret] = useState('')
  const [clearing, setClearing] = useState(false)
  const [headers, setHeaders] = useState(Object.entries(view.gateway?.headers ?? {}).map(([k, v]) => `${k}: ${v}`).join('\n'))
  const [to, setTo] = useState('')
  const [answer, setAnswer] = useState<{ ok: boolean; text: string } | null>(null)
  const { busy, error, save } = useSave(
    useCallback(async () => {
      await onSaved()
      onClose()
    }, [onSaved, onClose]),
    flash,
  )
  // The test text reports in the drawer and leaves it open: its answer is what the
  // person came to read.
  const test = useSave(useCallback(() => {}, []))
  const set = (patch: Partial<SMSGateway>) => setGw({ ...gw, ...patch })

  const parsedHeaders = () =>
    Object.fromEntries(
      headers
        .split('\n')
        .map((line) => line.split(/:(.*)/s).map((p) => p.trim()))
        .filter(([k]) => k),
    )

  // Absent keeps the stored secret, '' clears it (internal/api/notifications.go).
  const secretToSend = clearing ? '' : secret === '' ? undefined : secret

  return (
    <InnerPage
      back={back}
      title="SMS gateway"
      onClose={onClose}
      onSubmit={() =>
        void save(() => api.saveSMSGateway({ ...gw, headers: parsedHeaders() }, secretToSend, organisation), 'Gateway saved')
      }
      valid={gw.url.trim() !== ''}
      busy={busy}
      dirty={gw.method !== (view.gateway?.method || 'POST') || clearing}
      submitLabel="Save gateway"
      extra={
        view.gatewayConfigured && (
          <Confirm
            ask="Remove gateway"
            question="Texts stop; waiting ones are dropped."
            yes="Remove"
            keep="Keep it"
            busy={busy}
            onYes={() => void save(() => api.removeSMSGateway(organisation), 'Gateway removed')}
          />
        )
      }
    >
      {error && <Notice>{error}</Notice>}
      <fieldset className="field">
        <legend>Request</legend>
        <Seg
          label="Request"
          value={gw.method}
          onChange={(method) => set({ method })}
          options={(['POST', 'GET'] as const).map((m) => ({ value: m, label: m }))}
        />
      </fieldset>
      <label className="field">
        <span title="Receives every customer's number. May hold {{to}}, {{text}}, {{secret}} and {{id}}, each escaped for an address.">Address</span>
        <Input fullWidth value={gw.url} onChange={(e) => set({ url: e.target.value })} placeholder="https://sms.example.az/send" />
      </label>
      {gw.method === 'POST' && (
        <>
          <div className="field">
            <span>Body type</span>
            <ChoiceSelect
              label="Body type"
              value={gw.contentType ?? 'application/json'}
              onChange={(contentType) => set({ contentType })}
              options={[
                { value: 'application/json', label: 'JSON' },
                { value: 'application/x-www-form-urlencoded', label: 'A form' },
                { value: 'text/plain', label: 'Plain text' },
              ]}
            />
          </div>
          <label className="field">
            <span title="{{to}}, {{text}}, {{secret}} and {{id}} are escaped for the body's kind.">Body</span>
            <TextArea fullWidth className="org-textarea org-mono" rows={3} value={gw.body ?? ''} onChange={(e) => set({ body: e.target.value })} />
          </label>
        </>
      )}
      <label className="field">
        <span title="One per line, Name: value.">Headers</span>
        <TextArea
          fullWidth
          className="org-textarea org-mono"
          rows={2}
          value={headers}
          onChange={(e) => setHeaders(e.target.value)}
          placeholder="Authorization: Bearer {{secret}}"
        />
      </label>
      <label className="field">
        <span>Success text</span>
        <Input fullWidth className="org-mono" value={gw.successMatch ?? ''} onChange={(e) => set({ successMatch: e.target.value })} />
      </label>
      <Toggle
        title="Drop the +"
        hint="As 994501234567."
        checked={!!gw.stripPlus}
        onChange={(stripPlus) => set({ stripPlus })}
      />
      <Toggle
        title="Retry unanswered"
        hint="Only if the gateway drops a repeated {{id}}."
        checked={!!gw.retryUnconfirmed}
        onChange={(retryUnconfirmed) => set({ retryUnconfirmed })}
      />
      <label className="field">
        <span>Secret</span>
        <Input
          fullWidth
          type="password"
          autoComplete="new-password"
          value={secret}
          disabled={clearing}
          onChange={(e) => setSecret(e.target.value)}
          placeholder={clearing ? 'Cleared on save' : view.secretSet ? 'Stored; empty keeps it' : 'None stored'}
        />
        {view.secretSet &&
          (clearing ? (
            <Button variant="ghost" size="sm" onPress={() => setClearing(false)}>
              Keep the secret
            </Button>
          ) : (
            <Button
              variant="ghost"
              size="sm"
              onPress={() => {
                setSecret('')
                setClearing(true)
              }}
            >
              Clear secret
            </Button>
          ))}
      </label>
      <fieldset className="field">
        <legend>Send a test text</legend>
        <div className="os-test">
          <Input
            fullWidth
            type="tel"
            value={to}
            onChange={(e) => {
              setTo(e.target.value)
              setAnswer(null)
            }}
            placeholder="+994 50 123 45 67"
            aria-label="Number to test"
          />
          <Button
            variant="secondary"
            isDisabled={test.busy || busy || !to.trim()}
            onPress={() =>
              void test.save(async () => {
                const r = await api.testSMS(to, organisation)
                setAnswer(r.delivered ? { ok: true, text: 'Accepted by the gateway.' } : { ok: false, text: `Not sent: ${r.answer ?? ''}` })
              })
            }
          >
            Send test
          </Button>
        </div>
        {test.error && <Notice>{test.error}</Notice>}
        {answer && <small className={answer.ok ? 'os-good' : 'os-bad'}>{answer.text}</small>}
      </fieldset>
    </InnerPage>
  )
}
